import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { GalleryImage } from '@couch/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../api/client';
import { MACHINES_KEY } from './machines/MachineActions';
import { RECIPES_KEY } from './RecipesPage';

export const GALLERY_KEY = ['gallery'] as const;

function Tile({ image }: { image: GalleryImage }) {
  return (
    <Link to={`/gallery/${image.id}`} className="gallery-tile" aria-label={`${image.recipeTitle} — ${new Date(image.createdAt).toLocaleString()}`}>
      {image.hasThumb ? <img src={image.thumbUrl} alt="" loading="lazy" /> : <span className="gallery-placeholder">{image.kind === 'video' ? '▶ Video' : 'No preview'}</span>}
    </Link>
  );
}

export function GalleryPage() {
  const [recipeId, setRecipeId] = useState('');
  const [machineId, setMachineId] = useState('');
  const recipes = useQuery({ queryKey: RECIPES_KEY, queryFn: api.listRecipes });
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  const gallery = useInfiniteQuery({
    queryKey: [...GALLERY_KEY, recipeId, machineId],
    queryFn: ({ pageParam }) => api.listGallery({ recipeId, machineId, ...(pageParam ? { before: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
  const images = gallery.data?.pages.flatMap((p) => p.images) ?? [];
  const space = gallery.data?.pages[0]?.space;
  return (
    <section aria-labelledby="gallery-title">
      <div className="page-head"><h1 id="gallery-title">Gallery</h1></div>
      {/* doc 65: previews live on Couch within the user's space; the full pictures stay on the machines. */}
      {space && (
        <p className="muted gallery-space">
          Previews: {space.usedMb} of {space.limitMb} MB used — when it is full, the oldest previews are removed first. Full-size
          pictures stay on your machines; use Download original to keep one.
        </p>
      )}
      <div className="recipe-pickers">
        <label>Workflow{' '}
          <select value={recipeId} onChange={(e) => { setRecipeId(e.target.value); }}>
            <option value="">All</option>
            {recipes.data?.recipes.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
        </label>
        <label>Machine{' '}
          <select value={machineId} onChange={(e) => { setMachineId(e.target.value); }}>
            <option value="">All</option>
            {machines.data?.machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
      </div>
      {gallery.isPending && <p role="status">Loading…</p>}
      {gallery.isError && <p role="alert" className="form-error">{errorMessage(gallery.error)}</p>}
      {gallery.isSuccess && images.length === 0 && <p className="muted">No images yet. Run a workflow — its images show up here, also while the machine is off.</p>}
      <div className="gallery-grid">{images.map((i) => <Tile key={i.id} image={i} />)}</div>
      {gallery.hasNextPage && (
        <p><button type="button" className="button-quiet" disabled={gallery.isFetchingNextPage} onClick={() => { void gallery.fetchNextPage(); }}>Load more</button></p>
      )}
    </section>
  );
}
